import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const faqCategories = [
  { id: 'building', label: 'Building Permission', icon: 'ri-building-2-line' },
  { id: 'layout', label: 'Layout Approval', icon: 'ri-layout-grid-line' },
  { id: 'lrs', label: 'LRS', icon: 'ri-map-2-line' },
  { id: 'bps', label: 'BPS', icon: 'ri-shield-check-line' },
  { id: 'masterplan', label: 'Master Plan & Zoning', icon: 'ri-road-map-line' },
  { id: 'portal', label: 'Online Services', icon: 'ri-global-line' },
];

const faqsData: Record<string, { question: string; answer: string }[]> = {
  building: [
    {
      question: 'How do I apply for building permission online?',
      answer: 'You can apply for building permission through the APDPMS portal (apdpms.ap.gov.in). Register as a citizen, select your UDA/Municipality, fill the application form with plot details, upload required documents (site plan, structural drawings, land documents), and pay the applicable fees online.',
    },
    {
      question: 'What documents are required for building permission?',
      answer: 'Required documents include: Land ownership documents (registered sale deed / pattadar passbook), Encumbrance Certificate (EC) for 13 years, Link documents, Site plan and building plan prepared by a licensed architect/engineer, Structural stability certificate, NOCs from relevant departments (fire, airport, pollution control if applicable), and Aadhaar card of the applicant.',
    },
    {
      question: 'What is the timeline for processing building permission?',
      answer: 'As per the Ease of Doing Business norms, building permissions for residential buildings up to 500 sq.m are processed within 21 working days through the instant approval system. For larger buildings, the timeline is 30-45 working days depending on the category and required NOCs.',
    },
    {
      question: 'What are the setback requirements for residential buildings?',
      answer: 'Setback requirements vary based on the plot size, building height, and the applicable master plan/development control rules. Generally, for plots up to 200 sq.m in municipal areas, the minimum setbacks are 1.5m on all sides. For larger plots and taller buildings, setbacks increase proportionally. Refer to A.P. Building Rules, 2017 for specific requirements.',
    },
    {
      question: 'Can I modify my approved building plan?',
      answer: 'Yes, you can apply for revision/modification of the approved building plan through APDPMS. If the modification is minor (within the permissible limits of the original approval), it can be processed as a revised plan. Major changes may require a fresh application.',
    },
  ],
  layout: [
    {
      question: 'What is the process for layout approval?',
      answer: 'Layout approval involves: (1) Apply through APDPMS with land documents and proposed layout plan, (2) Technical scrutiny by DTCP/UDA officials, (3) Site inspection and verification, (4) NOCs from required departments, (5) Payment of development charges, (6) Issue of layout approval with conditions. The developer must complete infrastructure (roads, drainage, water supply) before selling plots.',
    },
    {
      question: 'What is the minimum area required for layout approval?',
      answer: 'The minimum area requirement varies by location and UDA jurisdiction. Generally, a minimum of 1000 sq.m (0.25 acres) is required for residential layout approval. For layouts in UDA areas, the minimum may be 2000 sq.m or more as per the applicable regulations.',
    },
    {
      question: 'What percentage of open space is required in a layout?',
      answer: 'As per the Development Control Rules, layouts must provide: 10% of total area as parks and open spaces, and additional area for civic amenities. Roads must be of specified widths (9m minimum for internal roads, 12m for collector roads). The exact percentages may vary based on the total area and location.',
    },
    {
      question: 'What is the validity period of layout approval?',
      answer: 'Layout approval is typically valid for 3-5 years from the date of issue, within which the developer must complete the infrastructure development. Extension can be applied for before the expiry of the approval period.',
    },
  ],
  lrs: [
    {
      question: 'What is the Layout Regularisation Scheme (LRS)?',
      answer: 'LRS is a scheme by the Government of Andhra Pradesh to regularise layouts that were developed without obtaining necessary approvals from DTCP/UDA. The scheme allows plot owners in such layouts to obtain regularisation by paying prescribed charges, subject to certain conditions.',
    },
    {
      question: 'Who is eligible to apply under LRS?',
      answer: 'Plot owners in unapproved/unauthorized layouts that were formed before the cut-off date specified in the relevant Government Order are eligible. The plot must be registered in the name of the applicant and should not be located in prohibited areas (water bodies, government land, road margins, etc.).',
    },
    {
      question: 'How are LRS charges calculated?',
      answer: 'LRS charges are calculated based on the market value of the land as per the registration department rates, the area of the plot, and the location (municipal/UDA/panchayat). The percentage varies from 15% to 25% of the market value depending on the GO provisions.',
    },
    {
      question: 'How to apply for LRS?',
      answer: 'Applications are submitted online through the DTCP LRS portal. Required documents include registered sale deed, property tax receipts, Aadhaar card, and a sketch of the layout. The application is processed through field verification, technical scrutiny, and approval by the competent authority.',
    },
  ],
  bps: [
    {
      question: 'What is the Building Penalisation Scheme (BPS)?',
      answer: 'BPS is a scheme to regularise buildings constructed without permission or in deviation from the approved building plan. Eligible building owners can regularise their structures by paying a penalisation fee calculated based on the nature and extent of violations.',
    },
    {
      question: 'What types of violations are covered under BPS?',
      answer: 'BPS covers: construction without building permission, deviation from approved plan (additional floors, reduced setbacks, excess built-up area), change of use from approved plan, and encroachment into setback areas. Buildings on government land, road margins, or structurally unsafe buildings are generally not eligible.',
    },
    {
      question: 'How is the BPS penalty calculated?',
      answer: 'The penalty is calculated based on the nature of violation, extent of deviation, location of the building, and the prevailing market value. The penalty ranges from a percentage of the regularisation charges specified in the relevant GO. Structural violations attract higher penalties.',
    },
    {
      question: 'What happens if I do not apply under BPS?',
      answer: 'Buildings with violations that are not regularised under BPS may face demolition orders from the municipal/UDA authorities. The scheme provides a one-time opportunity to regularise violations. After the scheme period, the authorities may initiate demolition proceedings.',
    },
  ],
  masterplan: [
    {
      question: 'What is a Master Plan?',
      answer: 'A Master Plan is a comprehensive long-term plan prepared by DTCP for the planned development of an urban area. It designates land use zones (residential, commercial, industrial, recreational, etc.), road network, transportation plan, public utility zones, and development standards. Master Plans are prepared under the A.P. Urban Areas (Development) Act, 1975.',
    },
    {
      question: 'How can I check the zoning of my property?',
      answer: 'You can check the zoning of your property by visiting the DTCP office or the concerned UDA office with your property details. Some Master Plans are available online on the DTCP website. You need to identify your property location on the master plan map to determine the applicable zone and permitted uses.',
    },
    {
      question: 'Can I get Change of Land Use (CLU)?',
      answer: 'Change of Land Use from the designated zoning in the Master Plan can be applied for through DTCP. The application is considered based on the surrounding development, impact on infrastructure, and overall planning objectives. CLU is generally granted for changes that are compatible with the surrounding land use.',
    },
    {
      question: 'How often are Master Plans revised?',
      answer: 'Master Plans are typically prepared for a 20-25 year perspective period and are revised every 10 years or as required. The revision process involves surveys, analysis of growth trends, public consultations, and approval by the government.',
    },
  ],
  portal: [
    {
      question: 'How do I register on the APDPMS portal?',
      answer: 'Visit apdpms.ap.gov.in and click on "Citizen Registration". Provide your Aadhaar number, mobile number, and email for OTP verification. Once verified, create a username and password. You can then log in to apply for building permissions, layout approvals, and track application status.',
    },
    {
      question: 'How can I track my application status?',
      answer: 'Log in to the APDPMS portal with your credentials. Go to "My Applications" to view all submitted applications and their current status. You can also track applications using the application number without logging in through the "Track Application" feature on the homepage.',
    },
    {
      question: 'What payment methods are available for fee payment?',
      answer: 'The APDPMS portal accepts payments through Net Banking, Debit Card, Credit Card, and UPI. Payment receipts are generated instantly and can be downloaded from the portal. All payments are secured through the AP government payment gateway.',
    },
    {
      question: 'How do I download my approved building permit?',
      answer: 'Once your building permission is approved, log in to APDPMS and go to "My Applications". Click on the approved application to view the permit. You can download the approved building plan and permission order as PDF documents. Digitally signed copies are valid for construction.',
    },
    {
      question: 'What should I do if I face technical issues on the portal?',
      answer: 'For technical support, you can: (1) Call the DTCP Helpdesk at 0863-2000662, (2) Email dtcp-ap@gov.in with your issue details and screenshots, (3) Visit the nearest UDA/DTCP office for in-person assistance. The helpdesk is available on working days from 10:00 AM to 5:00 PM.',
    },
  ],
};

const FaqItem: React.FC<{ question: string; answer: string; isOpen: boolean; onToggle: () => void }> = ({
  question, answer, isOpen, onToggle,
}) => (
  <div className="border border-stone-200 rounded-lg overflow-hidden transition-all duration-200 hover:border-amber-300">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-5 py-4 text-left bg-white hover:bg-amber-50/50 transition-colors"
    >
      <span className="text-stone-800 font-medium pr-4">{question}</span>
      <i className={`ri-arrow-down-s-line text-amber-600 text-lg transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}></i>
    </button>
    <div
      className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
    >
      <div className="px-5 pb-4 text-sm text-stone-600 leading-relaxed border-t border-stone-100 pt-3">
        {answer}
      </div>
    </div>
  </div>
);

export const FaqsPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('building');
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = faqsData[activeCategory] || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-stone-500 mb-8">
        <a href="/" className="hover:text-amber-700 transition-colors">{t('header.home')}</a>
        <i className="ri-arrow-right-s-line text-stone-400"></i>
        <a href="/resources" className="hover:text-amber-700 transition-colors">{t('header.resources')}</a>
        <i className="ri-arrow-right-s-line text-stone-400"></i>
        <span className="text-stone-800 font-medium">{t('header.resourcesFaqs')}</span>
      </nav>

      {/* Page Header */}
      <div className="mb-10">
        <h1 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">{t('header.resourcesFaqs')}</h1>
        <p className="text-lg text-stone-600 max-w-3xl">
          Find answers to commonly asked questions about DTCP services, building permissions, layout approvals, and online portals.
        </p>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-8">
        {faqCategories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => { setActiveCategory(cat.id); setOpenIndex(0); }}
            className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              activeCategory === cat.id
                ? 'bg-amber-600 text-white shadow-sm'
                : 'bg-stone-100 text-stone-600 hover:bg-amber-100 hover:text-amber-700'
            }`}
          >
            <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
            <span>{cat.label}</span>
          </button>
        ))}
      </div>

      {/* FAQ Accordion */}
      <div className="space-y-3">
        {faqs.map((faq, index) => (
          <FaqItem
            key={`${activeCategory}-${index}`}
            question={faq.question}
            answer={faq.answer}
            isOpen={openIndex === index}
            onToggle={() => setOpenIndex(openIndex === index ? null : index)}
          />
        ))}
      </div>
    </div>
  );
};
